const Strategy = require('../strategy/strategy');
const Analysis = require('../analysis/analysis');
const Rules = require('../rules/index');
const Renameable = require('../../lib/util/behaviors/renameable');
const SampleDraw = require('./sample-draw');
const DataEditor = require('../ui/data-editor');
const ListItemMenu = require('../ui/list-item-menu');
const MenuItem = require('../ui/list-menu-item');
const Card = require('./card');
const Tactic = require('../strategy/tactic');
const Confirmation = require('../ui/confirmation');
const { Auth } = require('../../services');
const DeckStore = require('../user/deck-store');
const template = require('./deck.tpl').default;
const Alert = require('../ui/alert');
const Share = require('../ui/share');
const router = require('../navigation/router');

const Deck = DomClass(template, function DeckInit() {
	var _t = this;

	this.share_continuation = null;

	var onstartrename = Renameable.call(this, null, {
		filter: name => name.replace(/[*]/g, "⭑")
	});

	var onchange = on(this, 'change');
	var onremove = on(this, 'remove');
	var onclose = on(this, 'close');

	this.toJSON = () => ({
		$id: this.$id,
		name: this.name,
		cards: this.cards,
		strategy: this.strategy,
		publish: this.publish || false,
		lastPublished: this.publish ? new Date().toISOString() : this.lastPublished,
		lastSaved: new Date().toISOString(),
		author: this.author
	});

	this.toMinimalJSON = () => JSON.stringify({
		$id: this.$id,
		name: this.name,
		cards: this.cards,
		strategy: this.strategy,
		publish: this.publish || false,
		author: this.author
	});

	this.lock = function() {
		this.classList.add('locked');
		this.strategy.lock();
		this.cards.lock();
		this.analysis.lock();
		// this.rules.lock();
	};

	this.unlock = function() {
		this.classList.remove('locked');
		this.strategy.unlock();
		this.cards.unlock();
		this.analysis.unlock();
		// this.rules.unlock();
	};

	this.isLocked = () => this.classList.contains('locked');

	var refresh = function(item) {
		refreshCards();
		refreshStrategy();
		refreshDraw();
		refreshAnalysis();
		refreshRules();
		refreshSharing();
		onchange.fire(_t);
	};

	var refreshAnalysis = function() {
		_t.analysis.analyze(_t.strategy, _t.cards.cards, _t);
	};

	var refreshStrategy = function() {
		_t.strategy.recount(_t.cards);
	};

	var refreshCards = function() {
		_t.cards.setTactics(_t.strategy.tactics);
	};

	var refreshDraw = function() {
		_t.draw.importCards(_t.cards.cards);
	};

	var refreshRules = function() {
		_t.rules.assess(_t.cards.cards);
	};

	var refreshSharing = function() {
		if (_t.lastPublished && _t.$id) {
			const [ _idDate, _idRand ] = _t.$id.split('.');
			const shareId = `${new Date('9999-12-31') - _idDate}.${_idRand}`;
			_t.shareWidget.style.display = '';
			_t.shareWidget.getData = () => ({
				title: `Check out this MTG deck analysis ("${_t.name}")`,
				text: `Check out this MTG deck analysis ("${_t.name}")`,
				url: `?shared/${shareId}`
			});
		} else {
			_t.shareWidget.style.display = 'none';
		}
	};

	on(this.cards, 'change').then(refresh);
	on(this.strategy, 'change').then(refresh);

	this.cards.editRaw = () => this.editData();
	this.strategy.editRaw = () => this.editData();

	var padRight = function(str, characters, pad) {
		return (str + Array(characters).join(pad || ' ')).slice(0, characters);
	};

	/**
	 * 
	 * @param {*} line 
	 * @returns {{
	 * 	tactics: {
	 * 		[k in string]: {
	 * 			name: string,
	 * 			quantityNeeded: number,
	 * 			turnNeeded: number,
	 * 		}
	 * 	},
	 * 	isCommander: boolean,
	 * }}
	 */
	var extractTags = function(line) {
		const tags = (line || '').split(/\s*#/);

		let isCommander = false;
		let sideboarded = 0;
		const tactics = {};

		for (const tag of tags) {
			const tacticParts = tag
				.replace(/_/g, ' ')
				.match(/^(.+)\s+?(\(([0-9]+)@([0-9]+)\))?$/)
			;

			const sideboardParts = tag
				.trim()
				.toLowerCase()
				.match(/^__sideboarded\(([0-9]+)\)?$/);

			if (sideboardParts) {
				sideboarded = Number(sideboardParts[1]);
			} else if (tacticParts) {
				tactics[tacticParts[1]] = {
					name: tacticParts[1],
					quantityNeeded: tacticParts[3],
					turnNeeded: tacticParts[4]
				};
			} else if (tag.trim().toLowerCase() === '__commander') {
				isCommander = true;
			}
		}

		return { tactics, isCommander, sideboarded };
	};

	this.promptSharing = async function() {
		try {
			const user = await Auth.currentAuthenticatedUser();
			_t.author = user.username;
			let prompt;
			if (_t.publish) {
				prompt = `
					<h3>Publishing for this deck is <u>ENABLED</u>.</h3>
					<p>Edits to this deck are currently being published. You can stop publishing changes, but previous publications will remain public.</p>
					<p><b>Keep it enabled?</b></p>
				`;
			} else {
				prompt = `
					<h3>Publishing for this deck is <u>DISABLED</u>.</h3>
					<p>Enabling it will share edits to this deck publicly. You can stop publishing changes later, but published copies will remain public.</p>
					<p><b>Enable sharing?</b></p>
				`;
			}
			const confirm = new Confirmation({ prompt })
			on(confirm, 'yes').then(enableSharing);
			on(confirm, 'no').then(disableSharing);
		} catch {
			new Alert({
				message: [
					`<h4>Only authenticated users can share decks.</h4>`,
					`<p>Sign up to participate in the fun!</p>`
				].join('')
			});
		}
	};

	const enableSharing = async function() {
		if (!_t.publish) {
			_t.publish = true;
			refresh();
		}
	};

	const disableSharing = async function() {
		if (_t.publish) {
			_t.publish = false;
			refresh();
		}
	};

	this.remove = function() {
		const confirm = new Confirmation({
			prompt: `Remove ${_t.name} forever?`
		});
		on(confirm, 'yes').then(() => {
			DeckStore.remove(_t.$id).then(() => {
				router.path = 'decks';
			});
		});
	};

	this.getIndex = function() {
		var rv = {};
		this.cards.cards.forEach(function(card) {
			rv[card.name] = card;
		});
		return rv;
	};

	this.applyChanges = function(changes) {
		var index = this.getIndex();
		for (const change of changes) {
			const existing = index[change.card];
			if (existing) {
				// the `quantity` property is total quantity of cards in deck + sideboard.
				// and we never want the total quantity of cards of a type to decrease
				// as a result of applying changes. so, it will either remain the same
				// or *increase* to be able to support the new target [active] quantity.
				// the sideboard will then be adjusted to match the new [active] quantity.
				existing.quantity = Math.max(existing.quantity, change.quantity);
				existing.sideboarded = existing.quantity - change.quantity;
				existing.tactics = change.tactics || existing.tactics;
			} else {
				_t.cards.cards.push(new Card({
					quantity: change.quantity,
					name: change.card,
					tactics: change.tactics || {},
					isCommander: false
				}));
			}
		};
		refresh();
	};

	this.createNew = function(changes) {
		var newDeck = new Deck();
		newDeck.name = this.name + " - " + (new Date()).toLocaleString();
		newDeck.import(this.export());
		newDeck.applyChanges(changes);
		DeckStore.save(newDeck).then(function(id) {
			router.path = 'decks/' + id;
		});
	};

	this.setCards = function(cards) {
		this.cards.cards = cards.map(c => new Card({
			quantity: c.activeQuantity,
			name: c.name,
			tactics: c.tactics,
			isCommander: false
		}));
		refresh();
	};

	this.copyToNew = function() {
		this.createNew([]);
	};

	this.export = function() {
		var tactics = {};
		_t.strategy.tactics.forEach(function(t) {
			tactics[t.name] = {
				q: t.quantityNeeded,
				t: t.turnNeeded
			};
		});

		const tacticHashes = card => {
			var hashes = [];
			for (var t in card.tactics) {
				if (!t.match(/^__/) && card.tactics[t]) {
					hashes.push('#' + t.replace(/\s+/g, "_")
						+ '_(' + tactics[t].q + '@' + tactics[t].t + ')'
					);
				}
			}
			return hashes;
		};

		var data = _t.cards.cards.map(function(card) {
			return padRight(card.quantity, 5)
				+ card.name + "  "
				+ tacticHashes(card).join(' ')
				+ (card.sideboarded > 0 ? ` #__sideboarded(${card.sideboarded})` : '')
			;
		}).join("\r\n");

		if (_t.cards.commander?.name) {
			const commanderName = _t.cards.commander.name;
			const commanderHashes = tacticHashes(_t.cards.commander).join(' ');
			const commanderLine =
				`1 ${commanderName} ${commanderHashes} #__Commander`;
			data = data + "\n" + commanderLine;
		}

		return data;
	};

	this.import = function(data) {
		var lines = data.replace(/\r\n/, "\n").split(/\n/);
		var cards = [];
		var commander = null;
		var totalCards = 0;
		var errors = [];
		var allTactics = {};
		var contextualTactic = null;
		lines.forEach(function(line) {
			var cardParts = line.match(/^\s*([0-9]+)x?\s+([^#]+)(#.*)?\s*$/);
			var tactic = line.match(/^\s*(.+)\s+\(([0-9]+)\)\s*$/);

			if (cardParts) {
				var cardTactics = {};
				var cardTags = {};
				let sideboarded = 0;

				if (contextualTactic) {
					cardTactics[contextualTactic.name] = 1;
					allTactics[contextualTactic.name] = contextualTactic;
				} else {
					const {
						tactics,
						isCommander,
						sideboarded: __sideboarded
					} = extractTags(cardParts[3]);
					sideboarded = __sideboarded;
					cardTags = { isCommander };
					for (const name of Object.keys(tactics)) {
						allTactics[name] = tactics[name];
						cardTactics[name] = 1;
					};
				}

				var card = new Card({
					quantity: cardParts[1],
					name: cardParts[2].replace(/^\s+|\s+$/g, ''),
					tactics: cardTactics,
					isCommander: Boolean(cardTags.isCommander),
					sideboarded
				});

				if (card.isCommander) {
					card.quantity = 1;
					commander = card;
				} else {
					totalCards += Number(card.quantity);
					cards.push(card);
				}
			}

			if (tactic) {
				var name = tactic[1];
				var qty = tactic[2];
				contextualTactic = {
					name: name,
					qty: qty
				};
			}
		});

		_t.strategy.tactics = [];

		for (var t in allTactics) {
			var tactic = allTactics[t];
			if (tactic.qty) {
				tactic.quantityNeeded = 1;
				tactic.turnNeeded = Math.max(1, Math.floor(totalCards/tactic.qty) - 7);
				delete tactic.qty;
			}
			_t.strategy.tactics.push(new Tactic(tactic));
		}

		_t.cards.cards = [];
		_t.cards.cards = cards;
		_t.cards.commander = commander;
	};

	this.editData = () => {
		var data = this.export();
		_t.dataEditor.open({
			readonly: this.isLocked(),
			data: data,
			filename: _t.name + '.txt'
		});
	};

	on(this.dataEditor, 'save').then(function(data) {
		_t.import(data);
		_t.dataEditor.close();
	});

	this.__dom.name.onchange = () => { onchange.fire(this) };

	this.menu.items = [
		new MenuItem({name: 'Rename', target: function() { onstartrename.fire(); } }),
		new MenuItem({name: 'Make a Copy', target: function() { _t.copyToNew(); }}),
		new MenuItem({name: 'Edit Raw', target: function() { _t.editData(); }}),
		new MenuItem({name: 'Sharing', target: function() { _t.promptSharing(); }}),
		new MenuItem({name: 'REMOVE', target: function() { _t.remove(); }})
	].filter(function(item) {
		return item != null;
	});

	getNodes(_t, 'db:feature').forEach(function(feature) {
		feature['nav-context'] = _t.collection + '/' + _t.$id;
	});
	refresh();

});

module.exports = Deck;
